import {
  Box,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, { useRef, useImperativeHandle, forwardRef, useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import ArticleIcon from '@mui/icons-material/Article'
import CloseIcon from '@mui/icons-material/Close'
import DescriptionIcon from '@mui/icons-material/Description'
import DownloadIcon from '@mui/icons-material/Download'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import HistoryIcon from '@mui/icons-material/History'
import MedicationIcon from '@mui/icons-material/Medication'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import SettingsIcon from '@mui/icons-material/Settings'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import PrintIcon from '@mui/icons-material/Print'
import SendIcon from '@mui/icons-material/Send'
import VideocamIcon from '@mui/icons-material/Videocam'
import { PRESCRIPTION_BUILDER_VIEW_TYPES } from 'src/consumer/constants'
import { selectPrescriptionViewSettingsData } from 'src/clinic/reducers/prescription/prescriptionReducer'
import LeaveButton from 'src/mentor/components/MentorClinicSomeChamberAutoJoiningPage/components/LeaveButton'
import {
  LSPageContainer,
  LSVideoPartContainer,
  LSMainContent,
  LSRightContentContainer,
} from '../../LiveSessionPageStyles'
import { getMmssString } from '../../LiveSessionPageComponents'
import VideoCallContainer from '../VideoCall/VideoCallContainer'
import SessionTopTabs from '../TopTabs/SessionTopTabs'
import SessionDrawer from '../SessionDrawer/SessionDrawer'
import { LiveSessionHeaderDetails } from '../LiveSessionHeaderDetails'
import PrescriptionFooterComponent from 'src/mentor/components/PrescriptionMakingPage/layoutComponents/PrescriptionFooterComponent'
import { HotlineSectionSeparator } from 'src/consumer/components/HotlineChamberPage/HotlineChamberStyles'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import useResizable from 'src/consumer/components/LiveSessionPage/hooks/useResizable'
import { isPatientVideoRole } from '../../utils/liveSessionRoles'
import './mobileCallChrome.css'

// ---------------------------------------------------------------------------
// Mobile call chrome (practitioner): mirrors the mentor chamber's mobile video UX.
// Full-screen video + bottom-right dock; the report editor opens as a full-screen
// sheet while the video collapses to a small draggable picture-in-picture tile.
// ---------------------------------------------------------------------------
const PIP_WIDTH = 118
const PIP_HEIGHT = 158
const PIP_WIDTH_LANDSCAPE = 192
const PIP_HEIGHT_LANDSCAPE = 108
const PIP_EDGE_MARGIN = 8

// Shared styles for the bottom-right mobile control dock (label chip + round button) — same
// treatment as the chamber dock so the two surfaces feel identical.
const CALL_DOCK_LABEL_SX = {
  fontSize: '11px',
  fontWeight: 600,
  color: '#fff',
  px: 1,
  py: 0.25,
  borderRadius: '999px',
  background: 'rgba(15, 15, 18, 0.62)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  whiteSpace: 'nowrap',
  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
}
const CALL_DOCK_FAB_SX = {
  width: 46,
  height: 46,
  color: '#fff',
  background: 'rgba(15, 15, 18, 0.62)',
  backdropFilter: 'blur(12px) saturate(180%)',
  WebkitBackdropFilter: 'blur(12px) saturate(180%)',
  border: '1px solid rgba(255,255,255,0.22)',
  boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
  '&:hover': { background: 'rgba(15,15,18,0.78)' },
}
// The page header's purple gradient — reused on the sheet's sticky header so the sheet reads as
// part of the same page.
const SHEET_HEADER_GRADIENT =
  'linear-gradient(135deg, rgba(102, 126, 234, 0.98) 0%, rgba(118, 75, 162, 0.98) 100%)'
// Tabs hidden in the call-chrome sheet (chamber guidance): vitals + investigations are inline
// sections of the report editor itself, and chat has no place in the practitioner sheet.
const CALL_CHROME_HIDDEN_TAB_IDS = ['vitals', 'investigations', 'chat']

const SessionContainer = forwardRef(
  (
    {
      sessionStarted,
      menuItemsList,
      selectedMenuItem,
      setSelectedMenuItem,
      renderWhatINeed,
      screenWidth,
      userRole,
      joinAgoraCall,
      agoraRtcProps,
      agoraRtmProps,
      onCameraClose,
      onCameraOpen,
      onAudioClose,
      onAudioOpen,
      handleLeaveRoom,
      handleEndVideoCall,
      showRejoin,
      sessionRoutingDecision,
      drawerForMoreOptionsOpenMobile,
      setDrawerForMoreOptionsOpenMobile,
      chatProps,
      joinAgoraCallFunction,
      timeElapsedInSeconds,
      setTimeElapsedInSeconds,
      leaveRoom,
      hidePreviewButton,
      moveToPrintPrescriptionScreen,
      setMoveToPrintPrescriptionScreen,
      eventType,
      markedCompleted,
      auxiliaryData,
      bookedEventInfo,
      changeLayoutFunction,
      onContentDrawerOpenChange, // Callback to notify parent when drawer state changes
      onOpenDrawerChange, // Callback to expose handleOpenDrawer to parent
      therapyReportRef,
      prescriptionRef,
      screeningReportRef,
      assessmentReportRef,
      // Device permissions for camera preview
      selectedCamera,
      selectedMicrophone,
      userHasAllowedAudio,
      userHasAllowedVideo,
      useAppOwnedRtcSurface,
      microphoneError,
      cameraError,
      setUserHasAllowedAudio,
      setUserHasAllowedVideo,
      setGiveCameraPermissionDialogOpen,
      setGiveMicrophonePermissionDialogOpen,
      meetingSrc,
      // Available devices for settings dropdown
      availableCameras,
      availableMicrophones,
      setSelectedCamera,
      setSelectedMicrophone,
      // Support QR code from API
      supportQrCodeMediaId,
      // Practitioner mobile call chrome (chamber-style full-screen video + report sheet).
      // Computed in index.jsx so the hidden fixed header and this layout always agree.
      mobileCallChrome = false,
    },
    ref,
  ) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const [contentDrawerOpen, setContentDrawerOpen] = useState(false) // Drawer for right content on mobile

    // Measure the real fixed-header (patient bar + tabs + optional draft strip) height so the spacer
    // below it is exactly tall enough — a hard-coded 164px left a big empty gap under the tab bar.
    const [measuredHeaderHeight, setMeasuredHeaderHeight] = useState(null)
    useEffect(() => {
      if (!isMobile) {
        setMeasuredHeaderHeight(null)
        return undefined
      }
      const tabsEl = document.querySelector('.session-top-tabs')
      const headerEl = tabsEl?.parentElement // the fixed Box wrapping the patient bar + tabs
      if (!headerEl) return undefined
      const measure = () => setMeasuredHeaderHeight(Math.ceil(headerEl.getBoundingClientRect().height))
      measure()
      if (typeof ResizeObserver === 'undefined') return undefined
      const ro = new ResizeObserver(measure)
      ro.observe(headerEl)
      return () => ro.disconnect()
    }, [isMobile, sessionStarted, selectedMenuItem])
    
    // Use ref to store the callback function to avoid infinite loops
    // Initialize ref with the callback (captured on first render only)
    const onContentDrawerOpenChangeRef = React.useRef(onContentDrawerOpenChange)
    const hasExposedCallbackRef = React.useRef(false)
    
    // Expose drawer open function to parent via callback (only once on mount)
    // We use the initial callback value and don't update it to prevent infinite loops
    React.useEffect(() => {
      if (onContentDrawerOpenChangeRef.current && !hasExposedCallbackRef.current) {
        hasExposedCallbackRef.current = true
        // Create a stable function that opens the drawer
        // Wrap state update in setTimeout to ensure it runs after render phase
        const openDrawerFn = () => {
          setTimeout(() => {
            setContentDrawerOpen(true)
          }, 0)
        }
        // Use requestAnimationFrame to defer the callback until after render completes
        // This prevents React warnings about updating components during render
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (onContentDrawerOpenChangeRef.current) {
              onContentDrawerOpenChangeRef.current(openDrawerFn)
            }
          })
        })
      }
      // Only run once on mount - don't include onContentDrawerOpenChange in deps
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Empty dependency array - only run on mount
    
    // Close drawer when session starts (drawer should be closed by default)
    // Also close drawer when sessionStarted changes from false to true (when joining)
    React.useEffect(() => {
      if (sessionStarted) {
        setContentDrawerOpen(false)
      }
    }, [sessionStarted])
    
    // Ensure drawer is closed on initial mount (mobile drawer should start closed)
    // Removed this useEffect as it conflicts with the callback exposure above
    // The drawer is already initialized to false via useState(false)
    
    const prescriptionViewSettings = useSelector(selectPrescriptionViewSettingsData)
    const prescriptionViewSettingsData = prescriptionViewSettings?.data

    const isTabletView =
      prescriptionViewSettingsData?.prescriptionBuilderViewType ===
      PRESCRIPTION_BUILDER_VIEW_TYPES.tabletView
    const hasVideoCall = menuItemsList?.some((item) => item?.id === 'video_call')
    const isPatientVideoExperience = hasVideoCall && isPatientVideoRole(userRole)

    // Refs for split-pane resize functionality
    const refBoxLeft = useRef()
    const refBoxRight = useRef()
    const refDragger = useRef()

    // Enable split-pane resizing when video call is available
    const { isResizing } = useResizable({
      refDragger,
      refLeftPanel: refBoxLeft,
      refRightPanel: refBoxRight,
      minLeftWidth: 280,
      minRightWidth: 360,
      disabled: !hasVideoCall || isPatientVideoExperience,
    })

    // ----- Practitioner mobile call chrome (chamber-style) -----
    // Full-screen video by default; the report editor lives in a full-screen sheet that stays
    // MOUNTED when closed (display:none) so the editor's refs + unsaved state survive toggling.
    const [mobileSheetOpen, setMobileSheetOpen] = useState(false)
    const [pipPos, setPipPos] = useState(null) // null → default top-centre
    const [pipLandscape, setPipLandscape] = useState(false)
    const pipDragRef = useRef(null)
    const [callDockMinimized, setCallDockMinimized] = useState(false)
    const [keyboardOpen, setKeyboardOpen] = useState(false)
    const [printMenuAnchor, setPrintMenuAnchor] = useState(null)
    const [sheetMenuAnchor, setSheetMenuAnchor] = useState(null)

    // Session timer: the fixed page header (which owns the visible timer) is hidden in call
    // chrome, so track elapsed time here the same timestamp-based way — it stays correct even if
    // intervals pause in a background tab.
    const [callElapsedSeconds, setCallElapsedSeconds] = useState(0)
    const callStartTsRef = useRef(null)
    useEffect(() => {
      if (!sessionStarted) {
        callStartTsRef.current = null
        setCallElapsedSeconds(0)
        return undefined
      }
      if (!mobileCallChrome) return undefined
      if (!callStartTsRef.current) {
        callStartTsRef.current = Date.now() - (timeElapsedInSeconds || 0) * 1000
      }
      const tick = () =>
        setCallElapsedSeconds(Math.floor((Date.now() - callStartTsRef.current) / 1000))
      tick()
      const id = setInterval(tick, 1000)
      return () => clearInterval(id)
      // timeElapsedInSeconds only seeds the baseline once — re-running on its updates would skew it
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mobileCallChrome, sessionStarted])
    const isOverDuration = callElapsedSeconds > (bookedEventInfo?.eventDuration * 60 || 0)

    // Leave from the call chrome records the elapsed time (the hidden header normally does this)
    // before running the page's leave flow.
    const leaveFromCallChrome = useCallback(
      async (markAsCompleted = false) => {
        if (setTimeElapsedInSeconds && callStartTsRef.current) {
          setTimeElapsedInSeconds(Math.floor((Date.now() - callStartTsRef.current) / 1000))
        }
        await handleLeaveRoom(markAsCompleted)
      },
      [handleLeaveRoom, setTimeElapsedInSeconds],
    )

    // One join, not two: "Join Consultation" already collected camera/mic choices on the join
    // screen, so in call chrome the video call starts automatically — the practitioner should
    // never meet a second "Join Video Call" page. Exceptions: sessions already marked completed
    // (they come back to finish the REPORT — video stays off until asked for) and a call the
    // practitioner deliberately ended (the once-per-session guard won't re-fire).
    const autoJoinAttemptedRef = useRef(false)
    useEffect(() => {
      if (!sessionStarted) {
        autoJoinAttemptedRef.current = false
        return
      }
      if (!mobileCallChrome || userRole !== 'mentor') return
      if (autoJoinAttemptedRef.current) return
      if (joinAgoraCall) {
        autoJoinAttemptedRef.current = true
        return
      }
      if (markedCompleted) return
      autoJoinAttemptedRef.current = true
      joinAgoraCallFunction?.()
    }, [
      mobileCallChrome,
      sessionStarted,
      joinAgoraCall,
      markedCompleted,
      joinAgoraCallFunction,
      userRole,
    ])

    // Leaving call chrome (rotate/resize to desktop, session end) resets the sheet + PIP.
    useEffect(() => {
      if (!mobileCallChrome) {
        setMobileSheetOpen(false)
        setPipPos(null)
      }
    }, [mobileCallChrome])
    useEffect(() => {
      if (!mobileSheetOpen) setPipPos(null)
    }, [mobileSheetOpen])

    // Detect the on-screen keyboard via visualViewport: "open" only when the viewport has shrunk
    // meaningfully AND an editable field is focused (the collapsing browser address bar shrinks the
    // viewport the same way) — then hide the floating dock so it never covers the focused input.
    useEffect(() => {
      if (!mobileCallChrome) return undefined
      const vv = typeof window !== 'undefined' ? window.visualViewport : null
      if (!vv) return undefined
      let baseline = vv.height
      const isEditableFocused = () => {
        const el = typeof document !== 'undefined' ? document.activeElement : null
        if (!el) return false
        const tag = el.tagName
        return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable === true
      }
      const update = () => {
        baseline = Math.max(baseline, vv.height)
        setKeyboardOpen(isEditableFocused() && baseline - vv.height > 150)
      }
      update()
      vv.addEventListener('resize', update)
      window.addEventListener('focusin', update)
      window.addEventListener('focusout', update)
      return () => {
        vv.removeEventListener('resize', update)
        window.removeEventListener('focusin', update)
        window.removeEventListener('focusout', update)
      }
    }, [mobileCallChrome])

    // Pin the floating controls to the VISIBLE (visual) viewport: position:fixed anchors to the
    // layout viewport, so with the browser toolbar shown, bottom-anchored controls can land behind
    // it. Expose the gap as CSS vars added to the fixed offsets (0 → no-op).
    useEffect(() => {
      if (!mobileCallChrome) return undefined
      const vv = typeof window !== 'undefined' ? window.visualViewport : null
      const root = typeof document !== 'undefined' ? document.documentElement : null
      if (!vv || !root) return undefined
      const apply = () => {
        const bottomInset = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop))
        const topInset = Math.max(0, Math.round(vv.offsetTop))
        root.style.setProperty('--ls-vv-bottom-inset', `${bottomInset}px`)
        root.style.setProperty('--ls-vv-top-inset', `${topInset}px`)
      }
      apply()
      vv.addEventListener('resize', apply)
      vv.addEventListener('scroll', apply)
      window.addEventListener('resize', apply)
      return () => {
        vv.removeEventListener('resize', apply)
        vv.removeEventListener('scroll', apply)
        window.removeEventListener('resize', apply)
        root.style.removeProperty('--ls-vv-bottom-inset')
        root.style.removeProperty('--ls-vv-top-inset')
      }
    }, [mobileCallChrome])

    // While the PIP is showing, follow the incoming (remote) video's orientation so a landscape
    // feed gets a 16:9 tile. Polls lightly since the stream can connect/rotate after opening.
    useEffect(() => {
      if (!mobileCallChrome || !mobileSheetOpen) return undefined
      const frame = refBoxLeft.current
      if (!frame) return undefined
      const check = () => {
        const videos = Array.from(frame.querySelectorAll('video')).filter(
          (v) => !v.closest('.shared-video-layout__self-preview') && v.videoWidth > 0,
        )
        if (videos.length === 0) return
        const main = videos.reduce((a, b) =>
          b.videoWidth * b.videoHeight > a.videoWidth * a.videoHeight ? b : a,
        )
        setPipLandscape(main.videoWidth >= main.videoHeight)
      }
      check()
      const id = setInterval(check, 1500)
      return () => clearInterval(id)
    }, [mobileCallChrome, mobileSheetOpen])

    // Picture-in-picture drag (pointer events, clamped to the viewport)
    const handlePipPointerDown = useCallback((e) => {
      const rect = e.currentTarget.getBoundingClientRect()
      pipDragRef.current = {
        pointerId: e.pointerId,
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
        width: rect.width,
        height: rect.height,
      }
      try {
        e.currentTarget.setPointerCapture(e.pointerId)
      } catch {
        /* setPointerCapture unsupported — drag still works via the captured listeners */
      }
    }, [])
    const handlePipPointerMove = useCallback((e) => {
      const drag = pipDragRef.current
      if (!drag) return
      const maxLeft = window.innerWidth - drag.width - PIP_EDGE_MARGIN
      const maxTop = window.innerHeight - drag.height - PIP_EDGE_MARGIN
      const left = Math.max(PIP_EDGE_MARGIN, Math.min(maxLeft, e.clientX - drag.offsetX))
      const top = Math.max(PIP_EDGE_MARGIN, Math.min(maxTop, e.clientY - drag.offsetY))
      setPipPos({ left, top })
    }, [])
    const handlePipPointerUp = useCallback((e) => {
      const drag = pipDragRef.current
      pipDragRef.current = null
      if (drag) {
        try {
          e.currentTarget.releasePointerCapture(drag.pointerId)
        } catch {
          /* no-op */
        }
      }
    }, [])

    // Simple drawer state - only changes on explicit user actions (button clicks, close button)
    // NO auto-open or auto-close logic
    const [drawerOpenState, setDrawerOpenStateInternal] = useState(false)
    
    // Wrapper to log all drawer state changes with stack trace
    const setDrawerOpen = React.useCallback((value) => {
      setDrawerOpenStateInternal(value)
    }, [])
    
    const drawerOpen = drawerOpenState
    
    // Get primary section (first non-video_call item) - loaded in main area
    const primarySection = React.useMemo(() => {
      if (isPatientVideoExperience) {
        return menuItemsList?.find((item) => item?.id === 'video_call') || null
      }
      const filteredMenuItems = menuItemsList?.filter((item) => item?.id !== 'video_call') || []
      return filteredMenuItems[0]
    }, [isPatientVideoExperience, menuItemsList])
    
    // Handler to open drawer when secondary button is clicked - called explicitly from button click
    // This is passed to SessionTopTabs via LiveSessionHeaderComponent
    const handleOpenDrawer = React.useCallback((itemId) => {
      // Assessments uses its own right-side PractitionerAssessmentDrawer; do not open the bottom
      // content drawer. In call chrome the sheet (z1500) would cover that drawer, so close it.
      if (itemId === 'assessments') {
        if (mobileCallChrome) setMobileSheetOpen(false)
        return
      }
      if (mobileCallChrome) {
        // Chat renders in the bottom SessionDrawer, which sits below the sheet — show it over the
        // video instead. Every other section swaps in place inside the report sheet.
        if (itemId === 'chat') {
          setMobileSheetOpen(false)
          setDrawerOpen(true)
          return
        }
        setDrawerOpen(false)
        setMobileSheetOpen(itemId !== 'video_call')
        return
      }
      if (isPatientVideoExperience) {
        setDrawerOpen(itemId !== 'video_call')
        return
      }
      // Check if it's primary section
      const filteredMenuItems = menuItemsList?.filter((item) => item?.id !== 'video_call') || []
      const primarySectionId = filteredMenuItems[0]?.id
      const isPrimary = primarySectionId === itemId
      
      // Only open drawer if it's NOT the primary section
      if (!isPrimary) {
        setDrawerOpen(true)
      } else {
        setDrawerOpen(false)
      }
    }, [isPatientVideoExperience, menuItemsList, setDrawerOpen, mobileCallChrome])
    
    // Track previous drawer state to detect when it closes
    const prevDrawerOpenRef = React.useRef(drawerOpen)
    
    // Reset selectedMenuItem to primary section when drawer closes
    // This ensures buttons don't remain highlighted when drawer closes.
    // While the call-chrome report sheet is open it owns the selection (tabs swap in-sheet and
    // programmatically close the drawer) — resetting here would stomp the just-picked tab.
    React.useEffect(() => {
      const drawerJustClosed = prevDrawerOpenRef.current === true && drawerOpen === false
      const sheetOwnsSelection = mobileCallChrome && mobileSheetOpen
      const shouldReset =
        drawerJustClosed &&
        !sheetOwnsSelection &&
        selectedMenuItem &&
        primarySection?.id &&
        selectedMenuItem !== primarySection.id

      // Update ref for next render
      prevDrawerOpenRef.current = drawerOpen

      if (shouldReset) {
        setSelectedMenuItem(primarySection.id)
      }
    }, [drawerOpen, selectedMenuItem, primarySection, setSelectedMenuItem, mobileCallChrome, mobileSheetOpen])
    
    // Expose handleOpenDrawer to parent via callback (similar to onContentDrawerOpenChange)
    // Use ref to track if we've already exposed the handler to prevent multiple calls
    const hasExposedHandlerRef = React.useRef(false)
    const onOpenDrawerChangeRef = React.useRef(onOpenDrawerChange)
    const lastMenuItemsListRef = React.useRef(null)
    
    // Update ref when callback changes
    React.useEffect(() => {
      onOpenDrawerChangeRef.current = onOpenDrawerChange
    }, [onOpenDrawerChange])
    
    // Only expose handler once when it's ready and menuItemsList is available.
    // Re-expose when menuItemsList actually changes OR when the handler's own identity changes
    // (e.g. the mobile call chrome toggling on a viewport flip) — otherwise the parent keeps
    // driving a STALE closure whose captured mode routes tab clicks the wrong way.
    const lastExposedHandlerRef = React.useRef(null)
    React.useEffect(() => {
      if (!onOpenDrawerChangeRef.current || !menuItemsList || menuItemsList.length === 0) {
        return
      }

      // Check if menuItemsList actually changed (compare IDs)
      const currentMenuIds = menuItemsList.map(item => item.id).join(',')
      const lastMenuIds = lastMenuItemsListRef.current
      const menuItemsChanged = currentMenuIds !== lastMenuIds
      const handlerChanged = lastExposedHandlerRef.current !== handleOpenDrawer

      if (!hasExposedHandlerRef.current || menuItemsChanged || handlerChanged) {
        onOpenDrawerChangeRef.current(handleOpenDrawer)
        hasExposedHandlerRef.current = true
        lastMenuItemsListRef.current = currentMenuIds
        lastExposedHandlerRef.current = handleOpenDrawer
      }
    }, [handleOpenDrawer, menuItemsList])
    
    useImperativeHandle(ref, () => ({}))

    // Fix aria-hidden accessibility issue for mobile drawer
    // When drawer is open and has focusable content, ensure backdrop doesn't have aria-hidden
    useEffect(() => {
      if (!contentDrawerOpen || !isMobile) return

      const timer = setTimeout(() => {
        // Find the drawer modal root by searching for the most recently opened drawer
        const drawers = document.querySelectorAll('.MuiDrawer-root.MuiDrawer-anchorBottom')
        const openDrawer = Array.from(drawers).find(drawer => {
          const modalRoot = drawer.closest('.MuiModal-root')
          return modalRoot && modalRoot.style.display !== 'none'
        })
        
        if (openDrawer) {
          const modalRoot = openDrawer.closest('.MuiModal-root')
          if (modalRoot) {
            const drawerPaper = modalRoot.querySelector('.MuiDrawer-paper')
            const hasFocusableContent = drawerPaper?.querySelector(
              'input:not([disabled]), button:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
            
            if (hasFocusableContent) {
              modalRoot.removeAttribute('aria-hidden')
              const backdrop = modalRoot.querySelector('.MuiBackdrop-root')
              if (backdrop) {
                backdrop.removeAttribute('aria-hidden')
              }
            }
          }
        }
      }, 100)

      return () => {
        clearTimeout(timer)
      }
    }, [contentDrawerOpen, isMobile])

    if (!sessionStarted) {
      return null
    }

    // Get therapy report status for draft badge
    const therapyReportStatus = therapyReportRef?.current?.therapyReportStatus || null

    // Call chrome: which section the report sheet shows. video_call/assessments/chat never render
    // in-sheet (the video is the page itself; the other two use their own drawers), so those fall
    // back to the primary section. The primary stays mounted (hidden) while a secondary shows, so
    // the report editor never loses unsaved state to a tab switch.
    const sheetSelectedId =
      !selectedMenuItem || ['video_call', 'assessments', 'chat'].includes(selectedMenuItem)
        ? primarySection?.id
        : selectedMenuItem
    const sheetShowsPrimary = sheetSelectedId === primarySection?.id

    return (
      <>
        {/* Main Content Area */}
        {/* Spacer to clear the fixed header + tabs. On mobile we use the measured header height (+4px
            breathing room) so there's no dead space; fall back to 164/148px before measurement. */}
        <Box
          sx={{
            height:
              isPatientVideoExperience || mobileCallChrome
                ? '0px'
                : isMobile
                  ? measuredHeaderHeight
                    ? `${measuredHeaderHeight + 4}px`
                    : '164px'
                  : '148px',
            width: '100%',
            flexShrink: 0,
          }}
        />
        <LSPageContainer sessionStarted={sessionStarted} style={{ marginTop: '0px', paddingTop: '0px' }}>
          {' '}
          {/* 60px header + 48px tabs */}
          <LSVideoPartContainer
            sessionStarted={sessionStarted}
            style={{
              gap: 0,
              overflow: sessionStarted ? 'hidden' : 'visible',
              overflowX: 'visible',
              overflowY: hasVideoCall && sessionStarted ? 'hidden' : 'auto',
              background: '#FFF',
              paddingTop: '0px',
              marginTop: '0px',
              minHeight: hasVideoCall ? 'unset' : 'auto',
              // Call chrome: the video box is position:fixed, so the in-flow stage just needs to
              // fill the (dynamic) viewport without creating scroll. dvh tracks the browser bars.
              height: mobileCallChrome
                ? '100dvh'
                : isPatientVideoExperience
                  ? '100vh'
                  : hasVideoCall
                    ? 'calc(100vh - 108px)'
                    : 'auto',
              maxHeight: mobileCallChrome
                ? '100dvh'
                : isPatientVideoExperience
                  ? '100vh'
                  : hasVideoCall
                    ? 'calc(100vh - 108px)'
                    : 'none',
              position: 'relative',
              alignItems: 'flex-start', // Always align to top, never center
              width: '100%',
              maxWidth: '100vw',
            }}
          >
            {hasVideoCall && (
              <div
                ref={refBoxLeft}
                // 'resizable-box' carries a global `position: relative !important` that would
                // defeat the fixed positioning the call chrome relies on — drop it there.
                className={
                  mobileCallChrome ? 'hide-on-print-only' : 'resizable-box hide-on-print-only'
                }
                data-video-container={mobileCallChrome ? 'ls-live' : undefined}
                data-video-pip={mobileCallChrome && mobileSheetOpen ? 'true' : 'false'}
                style={
                  mobileCallChrome
                    ? mobileSheetOpen
                      ? !joinAgoraCall
                        ? {
                            // Sheet open before the call starts: a PIP of the join screen is
                            // meaningless — hide the video box (still mounted) until in-call.
                            display: 'none',
                          }
                        : {
                          // Report sheet open → small draggable picture-in-picture tile floating
                          // above the sheet so the practitioner keeps eyes on the patient.
                          position: 'fixed',
                          ...(pipPos
                            ? { top: `${pipPos.top}px`, left: `${pipPos.left}px` }
                            : {
                                // Default to the bottom-left corner: the sheet's header, tab
                                // strip (top) and control dock (bottom-right) stay clear.
                                bottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)',
                                left: '10px',
                              }),
                          width: `${pipLandscape ? PIP_WIDTH_LANDSCAPE : PIP_WIDTH}px`,
                          height: `${pipLandscape ? PIP_HEIGHT_LANDSCAPE : PIP_HEIGHT}px`,
                          borderRadius: '16px',
                          border: '1.5px solid rgba(255, 255, 255, 0.85)',
                          boxShadow: '0 16px 40px rgba(0, 0, 0, 0.45)',
                          touchAction: 'none',
                          cursor: 'grab',
                          zIndex: 1700,
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                        }
                      : {
                          // Full-screen video pinned to the visible viewport (immune to 100vh
                          // quirks and the fixed header's in-flow spacer) — the call IS the page.
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          width: '100vw',
                          zIndex: 100,
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                        }
                    : {
                        // Mobile: Full width (right content is in drawer). Desktop: 50% split
                        width: isMobile || isPatientVideoExperience ? '100%' : '50%',
                        height: '100%',
                        maxHeight: '100%', // Ensure it doesn't exceed parent height
                        display: 'flex',
                        flexDirection: 'column',
                        flexShrink: 0, // Prevent flexbox from auto-resizing
                        overflow: 'hidden', // Prevent scrolling - controls use absolute positioning
                        position: 'relative', // Ensure proper positioning context for absolute children
                      }
                }
              >
                <VideoCallContainer
                  userRole={userRole}
                  mobileCallChrome={mobileCallChrome}
                  joinAgoraCall={joinAgoraCall}
                  agoraRtcProps={agoraRtcProps}
                  agoraRtmProps={agoraRtmProps}
                  onCameraClose={onCameraClose}
                  onCameraOpen={onCameraOpen}
                  onAudioClose={onAudioClose}
                  onAudioOpen={onAudioOpen}
                  handleLeaveRoom={handleLeaveRoom}
                  handleEndVideoCall={handleEndVideoCall}
                  showRejoin={showRejoin}
                  sessionRoutingDecision={sessionRoutingDecision}
                  screenWidth={screenWidth}
                  menuItemsList={menuItemsList}
                  selectedMenuItem={selectedMenuItem}
                  setSelectedMenuItem={setSelectedMenuItem}
                  renderWhatINeed={renderWhatINeed}
                  drawerForMoreOptionsOpenMobile={drawerForMoreOptionsOpenMobile}
                  setDrawerForMoreOptionsOpenMobile={setDrawerForMoreOptionsOpenMobile}
                  chatProps={chatProps}
                  joinAgoraCallFunction={joinAgoraCallFunction}
                  hasTopTabs={true} // New prop to indicate top tabs layout
                  bookedEventInfo={bookedEventInfo}
                  auxiliaryData={auxiliaryData}
                  peerChatName={chatProps?.peerChatName}
                  peerChatImage={chatProps?.peerChatImage}
                  changeLayoutFunction={changeLayoutFunction}
                  // Device permissions for camera preview
                  selectedCamera={selectedCamera}
                  selectedMicrophone={selectedMicrophone}
                  userHasAllowedAudio={userHasAllowedAudio}
                  userHasAllowedVideo={userHasAllowedVideo}
                  useAppOwnedRtcSurface={useAppOwnedRtcSurface}
                  microphoneError={microphoneError}
                  cameraError={cameraError}
                  setUserHasAllowedAudio={setUserHasAllowedAudio}
                  setUserHasAllowedVideo={setUserHasAllowedVideo}
                  setGiveCameraPermissionDialogOpen={setGiveCameraPermissionDialogOpen}
                  setGiveMicrophonePermissionDialogOpen={setGiveMicrophonePermissionDialogOpen}
                  meetingSrc={meetingSrc}
                  // Available devices for settings dropdown
                  availableCameras={availableCameras}
                  availableMicrophones={availableMicrophones}
                  setSelectedCamera={setSelectedCamera}
                  setSelectedMicrophone={setSelectedMicrophone}
                  // Support QR code from API
                  supportQrCodeMediaIdFromApi={supportQrCodeMediaId}
                />
                {/* Call chrome, full-screen video: top overlay with the patient context, session
                    timer and Leave — the page's fixed header (which normally carries these) is
                    hidden in this mode. pointerEvents pass through except on the controls. */}
                {mobileCallChrome && !mobileSheetOpen && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      zIndex: 30,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      pt: 'calc(env(safe-area-inset-top, 0px) + 8px + var(--ls-vv-top-inset, 0px))',
                      pb: '22px',
                      pointerEvents: 'none',
                      background:
                        'linear-gradient(180deg, rgba(10, 12, 24, 0.66) 0%, rgba(10, 12, 24, 0.38) 55%, rgba(10, 12, 24, 0) 100%)',
                      '@keyframes lsOverTimeBlink': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.2 },
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, pointerEvents: 'auto' }}>
                      <LiveSessionHeaderDetails
                        bookedEventFinalData={bookedEventInfo}
                        auxiliaryData={auxiliaryData}
                      />
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        flexShrink: 0,
                        fontFamily: '"Orbitron", "Roboto Mono", monospace',
                        fontSize: '15px',
                        fontWeight: 700,
                        letterSpacing: '1px',
                        color: isOverDuration ? '#ff4d4f' : '#ffffff',
                        textShadow: isOverDuration
                          ? '0 1px 6px rgba(255, 77, 79, 0.55)'
                          : '0 2px 8px rgba(0, 0, 0, 0.4)',
                        whiteSpace: 'nowrap',
                        animation: isOverDuration
                          ? 'lsOverTimeBlink 1s ease-in-out infinite'
                          : 'none',
                      }}
                    >
                      {getMmssString(callElapsedSeconds)}
                    </Box>
                    <Box sx={{ flexShrink: 0, pointerEvents: 'auto' }}>
                      <LeaveButton
                        leaveSession={leaveFromCallChrome}
                        isAlreadyMarkedAsCompleted={markedCompleted}
                        popoverZIndex={2200}
                        customButton={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              px: '13px',
                              py: '8px',
                              borderRadius: '999px',
                              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                              color: '#fff',
                              fontWeight: 700,
                              fontSize: '13px',
                              boxShadow: '0 6px 18px rgba(239, 68, 68, 0.45)',
                              border: '1px solid rgba(255, 255, 255, 0.35)',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <ExitToAppIcon sx={{ fontSize: 16 }} />
                            Leave
                          </Box>
                        }
                      />
                    </Box>
                  </Box>
                )}
                {/* PIP drag layer: makes the whole tile draggable and carries an expand button
                    that restores full-screen video (closes the sheet). The expand button stops
                    drag-start so a tap doesn't move the tile. */}
                {mobileCallChrome && mobileSheetOpen && joinAgoraCall && (
                  <Box
                    onPointerDown={handlePipPointerDown}
                    onPointerMove={handlePipPointerMove}
                    onPointerUp={handlePipPointerUp}
                    onPointerCancel={handlePipPointerUp}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 5,
                      cursor: 'grab',
                      touchAction: 'none',
                      '&:active': { cursor: 'grabbing' },
                      background:
                        'linear-gradient(180deg, rgba(0,0,0,0.34) 0%, rgba(0,0,0,0) 36%, rgba(0,0,0,0) 64%, rgba(0,0,0,0.34) 100%)',
                    }}
                  >
                    <DragIndicatorIcon
                      sx={{
                        position: 'absolute',
                        top: 3,
                        left: 3,
                        fontSize: 15,
                        color: 'rgba(255,255,255,0.85)',
                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
                        pointerEvents: 'none',
                      }}
                    />
                    {/* One-shot affordance hint: appears every time the tile mounts, fades out
                        after a beat. The grip icon stays as the persistent cue. */}
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        '@keyframes lsPipDragHint': {
                          '0%': { opacity: 0, transform: 'translateY(4px)' },
                          '12%': { opacity: 1, transform: 'translateY(0)' },
                          '72%': { opacity: 1 },
                          '100%': { opacity: 0 },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px',
                          px: '9px',
                          py: '4px',
                          borderRadius: '999px',
                          background: 'rgba(15, 15, 18, 0.75)',
                          border: '1px solid rgba(255, 255, 255, 0.25)',
                          color: '#fff',
                          fontSize: '10.5px',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          opacity: 0,
                          animation: 'lsPipDragHint 3.4s ease 0.3s forwards',
                        }}
                      >
                        <DragIndicatorIcon sx={{ fontSize: 13 }} />
                        Drag to move
                      </Box>
                    </Box>
                    <Tooltip title="Expand video" arrow>
                      <IconButton
                        aria-label="Expand video to full screen"
                        size="small"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => setMobileSheetOpen(false)}
                        sx={{
                          position: 'absolute',
                          top: 1,
                          right: 1,
                          color: '#fff',
                          width: 26,
                          height: 26,
                          '& svg': { fontSize: 15 },
                        }}
                      >
                        <OpenInFullIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </div>
            )}
            {/* Desktop: Resizer separator - Hidden on mobile (right content is in drawer) */}
            {hasVideoCall && userRole === 'mentor' && !isMobile && (
              <HotlineSectionSeparator
                ref={refDragger}
                className="left-resizer hide-on-print-only"
                style={{ cursor: 'ew-resize' }}
              >
                <DragIndicatorIcon style={{ fontSize: '15px' }} />
              </HotlineSectionSeparator>
            )}
            {/* Desktop: Right content container - Hidden on mobile (shown in drawer).
                In call chrome the content lives in the full-screen report sheet instead. */}
            {!isPatientVideoExperience && !mobileCallChrome && (
              <LSRightContentContainer
                ref={refBoxRight}
                hasVideoCall={hasVideoCall}
                sessionStarted={sessionStarted}
                style={{
                  // Mobile: Hide right content only if there's a video call (it's in drawer).
                  // For offline sessions (no video call), show content directly.
                  display: isMobile && hasVideoCall ? 'none' : undefined,
                }}
              >
                <LSMainContent
                  withoutSidebar={true}
                  sessionStarted={sessionStarted}
                  style={{
                    width: '100%',
                    maxWidth: '100vw',
                    height: hasVideoCall && sessionStarted ? '100%' : 'auto',
                    minHeight: 'auto',
                    overflowX: 'visible',
                    overflowY: 'visible',
                    boxSizing: 'border-box',
                    position: 'relative',
                    // Ensure content starts at top
                    scrollBehavior: 'auto',
                  }}
                >
                  {/* Always show primary section (Therapy Report) in main content area */}
                  {/* Secondary sections (History, Report Preview) only show in drawer */}
                  {primarySection?.id && renderWhatINeed(primarySection.id)}
                </LSMainContent>

                {isTabletView && (
                  <PrescriptionFooterComponent
                    endConsultation={leaveRoom}
                    timeElapsedInSeconds={timeElapsedInSeconds}
                    setTimeElapsedInSeconds={setTimeElapsedInSeconds}
                    sidebarSelectMenu={menuItemsList}
                    sidebarSelectedMenuItem={selectedMenuItem}
                    setSidebarSelectedMenuItem={(itemId) => {
                      setSelectedMenuItem(itemId)
                    }}
                    onPrescriptionPreviewPage={hidePreviewButton}
                    goToPrintPage={() => {
                      if (selectedMenuItem !== 'prescription') {
                        setSelectedMenuItem('prescription')
                      }
                      setMoveToPrintPrescriptionScreen(true)
                    }}
                  />
                )}
              </LSRightContentContainer>
            )}
          </LSVideoPartContainer>
        </LSPageContainer>

        {/* Session Drawer for Secondary Sections (History, Vitals History, Rx, etc.) */}
        {/* This drawer opens when any button except the first one (primary section) is clicked */}
        <SessionDrawer
          key={`drawer-${selectedMenuItem}`}
          open={drawerOpen}
              onClose={(event, reason) => {
                // Close drawer and switch back to primary section
                if (reason === 'backdropClick') {
                  const target = event?.target
                  const isClickOnButtons = target?.closest('.session-top-tabs')
                  if (isClickOnButtons) {
                    return // Don't close if clicking on buttons
                  }
                }
                
                // Switch back to primary section FIRST, then close drawer
                // This ensures the button highlight is reset immediately
                if (primarySection?.id) {
                  setSelectedMenuItem(primarySection.id)
                }
                // Then close the drawer
                setDrawerOpen(false)
              }}
          selectedMenuItem={selectedMenuItem}
          menuItemsList={menuItemsList}
          renderWhatINeed={renderWhatINeed}
          chatProps={chatProps}
        />

        {/* Mobile: Right Content Drawer (Therapy Report/Consultation/Prescription).
            Superseded by the full-screen report sheet in call chrome. */}
        {isMobile && hasVideoCall && !isPatientVideoExperience && !mobileCallChrome && (
          <Drawer
            anchor="bottom"
            open={contentDrawerOpen}
            onClose={() => setContentDrawerOpen(false)}
            ModalProps={{
              disableScrollLock: true,
            }}
            PaperProps={{
              sx: {
                height: '85vh',
                borderTopLeftRadius: '24px',
                borderTopRightRadius: '24px',
                background: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                display: 'flex',
                flexDirection: 'column',
              },
            }}
          >
            {/* Drawer Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                pb: 1.5,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                background: 'rgba(255, 255, 255, 0.98)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <DescriptionIcon sx={{ fontSize: 24, color: '#6779e3' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
                  {menuItemsList?.find((item) => item.id === selectedMenuItem)?.label || 
                   menuItemsList?.find((item) => item.id === selectedMenuItem)?.title || 
                   primarySection?.title || 
                   'Session Details'}
                </Typography>
              </Box>
              <IconButton
                onClick={() => setContentDrawerOpen(false)}
                sx={{
                  color: 'rgba(103, 121, 227, 0.8)',
                  backgroundColor: 'rgba(103, 121, 227, 0.1)',
                  border: '1px solid rgba(103, 121, 227, 0.2)',
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: 'rgba(103, 121, 227, 0.2)',
                    borderColor: 'rgba(103, 121, 227, 0.4)',
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Drawer Content */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                p: 2,
              }}
            >
              <LSMainContent
                withoutSidebar={true}
                sessionStarted={sessionStarted}
                style={{
                  width: '100%',
                  height: sessionStarted ? '100%' : 'auto',
                  flex: 1,
                }}
              >
                {/* Render content based on selected menu item (primary or secondary) */}
                {renderWhatINeed(selectedMenuItem)}
              </LSMainContent>

              {isTabletView && (
                <PrescriptionFooterComponent
                  endConsultation={leaveRoom}
                  timeElapsedInSeconds={timeElapsedInSeconds}
                  setTimeElapsedInSeconds={setTimeElapsedInSeconds}
                  sidebarSelectMenu={menuItemsList}
                  sidebarSelectedMenuItem={selectedMenuItem}
                  setSidebarSelectedMenuItem={(itemId) => {
                    setSelectedMenuItem(itemId)
                  }}
                  onPrescriptionPreviewPage={hidePreviewButton}
                  goToPrintPage={() => {
                    if (selectedMenuItem !== 'prescription') {
                      setSelectedMenuItem('prescription')
                    }
                    setMoveToPrintPrescriptionScreen(true)
                  }}
                />
              )}
            </Box>
          </Drawer>
        )}

        {/* ----- Practitioner mobile call chrome: report sheet + control dock ----- */}
        {mobileCallChrome && (
          <>
            {/* Full-screen report sheet. Stays MOUNTED when closed (display:none) so the editor's
                refs, autosave and unsaved fields survive toggling back to the video. The sheet
                itself never scrolls — the dedicated inner Box does. */}
            <Box
              className="ls-call-sheet"
              sx={{
                display: mobileSheetOpen ? 'flex' : 'none',
                flexDirection: 'column',
                position: 'fixed',
                inset: 0,
                width: '100%',
                height: '100dvh',
                maxHeight: '100dvh',
                zIndex: 1500,
                background: '#ffffff',
                overflow: 'hidden',
              }}
            >
              {/* Sticky sheet header: patient context + timer + Leave + back-to-video */}
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  pt: 'calc(env(safe-area-inset-top, 0px) + 8px)',
                  pb: '8px',
                  background: SHEET_HEADER_GRADIENT,
                  boxShadow: '0 4px 24px rgba(102, 126, 234, 0.25)',
                  zIndex: 2,
                  '@keyframes lsSheetOverTimeBlink': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.2 },
                  },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <LiveSessionHeaderDetails
                    bookedEventFinalData={bookedEventInfo}
                    auxiliaryData={auxiliaryData}
                  />
                </Box>
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    fontFamily: '"Orbitron", "Roboto Mono", monospace',
                    fontSize: '13px',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    color: isOverDuration ? '#ff4d4f' : '#ffffff',
                    whiteSpace: 'nowrap',
                    animation: isOverDuration
                      ? 'lsSheetOverTimeBlink 1s ease-in-out infinite'
                      : 'none',
                  }}
                >
                  {getMmssString(callElapsedSeconds)}
                </Box>
                <Box sx={{ flexShrink: 0 }}>
                  <LeaveButton
                    leaveSession={leaveFromCallChrome}
                    isAlreadyMarkedAsCompleted={markedCompleted}
                    popoverZIndex={2200}
                    customButton={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          px: '11px',
                          py: '7px',
                          borderRadius: '999px',
                          background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '12.5px',
                          boxShadow: '0 4px 14px rgba(239, 68, 68, 0.45)',
                          border: '1px solid rgba(255, 255, 255, 0.35)',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <ExitToAppIcon sx={{ fontSize: 15 }} />
                        Leave
                      </Box>
                    }
                  />
                </Box>
                {/* Back-to-video lives on the dock toggle — the header stays: patient · timer ·
                    Leave · ⋮ and nothing else. */}
                <IconButton
                  aria-label="More options"
                  onClick={(e) => setSheetMenuAnchor(e.currentTarget)}
                  sx={{
                    flexShrink: 0,
                    color: '#ffffff',
                    width: 30,
                    height: 30,
                    ml: '-4px',
                  }}
                >
                  <MoreVertIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>

              {/* Sheet ⋮ menu — settings for the report being edited (the fixed page-side action
                  dock that used to carry these is hidden inside the sheet, chamber-style). */}
              <Menu
                anchorEl={sheetMenuAnchor}
                open={Boolean(sheetMenuAnchor)}
                onClose={() => setSheetMenuAnchor(null)}
                sx={{ zIndex: 2000 }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { minWidth: 200, borderRadius: '12px' } }}
              >
                <MenuItem
                  onClick={() => {
                    setSheetMenuAnchor(null)
                    if (primarySection?.id === 'prescription') {
                      prescriptionRef?.current?.openSettings?.()
                    } else if (primarySection?.id === 'therapy_report') {
                      therapyReportRef?.current?.openSettings?.()
                    } else if (primarySection?.id === 'screening_report') {
                      screeningReportRef?.current?.openSettings?.()
                    } else if (primarySection?.id === 'assessment_report') {
                      assessmentReportRef?.current?.openSettings?.()
                    }
                  }}
                >
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    {primarySection?.id === 'prescription' ? 'Page settings' : 'Report settings'}
                  </ListItemText>
                </MenuItem>
              </Menu>

              {/* Section tabs (same tab bar the fixed header shows outside call chrome) */}
              <Box sx={{ flexShrink: 0 }}>
                {/* Tabs only — no Save/Print/Settings row: saving happens on Leave (and before
                    any print), printing lives in the dock, settings in the header ⋮ menu. */}
                <SessionTopTabs
                  menuItemsList={menuItemsList}
                  selectedMenuItem={selectedMenuItem}
                  setSelectedMenuItem={setSelectedMenuItem}
                  therapyReportStatus={therapyReportStatus}
                  onOpenDrawer={handleOpenDrawer}
                  showPrimaryTabOnMobile
                  hiddenTabIds={CALL_CHROME_HIDDEN_TAB_IDS}
                />
              </Box>

              {/* Dedicated scroller: the sheet is overflow:hidden, content scrolls here */}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  width: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <LSMainContent
                  withoutSidebar={true}
                  sessionStarted={sessionStarted}
                  style={{
                    width: '100%',
                    maxWidth: '100vw',
                    height: 'auto',
                    minHeight: '100%',
                    overflowX: 'visible',
                    overflowY: 'visible',
                    boxSizing: 'border-box',
                    position: 'relative',
                  }}
                >
                  {/* Primary section stays mounted; secondary sections render alongside */}
                  <Box sx={{ width: '100%', display: sheetShowsPrimary ? 'block' : 'none' }}>
                    {primarySection?.id && renderWhatINeed(primarySection.id)}
                  </Box>
                  {!sheetShowsPrimary && sheetSelectedId && (
                    <Box sx={{ width: '100%' }}>
                      {renderWhatINeed(sheetSelectedId, () =>
                        setSelectedMenuItem(primarySection?.id),
                      )}
                    </Box>
                  )}
                </LSMainContent>

                {isTabletView && (
                  <PrescriptionFooterComponent
                    endConsultation={leaveRoom}
                    timeElapsedInSeconds={timeElapsedInSeconds}
                    setTimeElapsedInSeconds={setTimeElapsedInSeconds}
                    sidebarSelectMenu={menuItemsList}
                    sidebarSelectedMenuItem={selectedMenuItem}
                    setSidebarSelectedMenuItem={(itemId) => {
                      setSelectedMenuItem(itemId)
                    }}
                    onPrescriptionPreviewPage={hidePreviewButton}
                    goToPrintPage={() => {
                      if (selectedMenuItem !== 'prescription') {
                        setSelectedMenuItem('prescription')
                      }
                      setMoveToPrintPrescriptionScreen(true)
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Bottom-right control dock (hidden while the keyboard is up so it never covers the
                focused field). Same layout/looks as the chamber dock. */}
            {!keyboardOpen && (
              <Box
                sx={{
                  position: 'fixed',
                  right: '12px',
                  bottom:
                    'calc(env(safe-area-inset-bottom, 0px) + 18px + var(--ls-vv-bottom-inset, 0px))',
                  zIndex: 1800,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 1.25,
                }}
              >
                {!callDockMinimized && (
                  <>
                    {/* Print — prescription gets a Print/Download/Send menu; report sessions open
                        their native preview (screening) or the report-preview tab. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography component="span" sx={CALL_DOCK_LABEL_SX}>
                        Print
                      </Typography>
                      <IconButton
                        aria-label="Print or download"
                        onClick={(e) => {
                          if (primarySection?.id === 'prescription') {
                            setPrintMenuAnchor(e.currentTarget)
                          } else if (
                            primarySection?.id === 'screening_report' &&
                            screeningReportRef?.current?.openPreviewDrawer
                          ) {
                            setMobileSheetOpen(true)
                            screeningReportRef.current.openPreviewDrawer()
                          } else {
                            setSelectedMenuItem('report-preview')
                            setDrawerOpen(false)
                            setMobileSheetOpen(true)
                          }
                        }}
                        sx={CALL_DOCK_FAB_SX}
                      >
                        <PrintIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Box>
                    {/* History — opens the patient-history section in the sheet */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography component="span" sx={CALL_DOCK_LABEL_SX}>
                        History
                      </Typography>
                      <IconButton
                        aria-label="Patient history"
                        onClick={() => {
                          setSelectedMenuItem('patient_history')
                          setDrawerOpen(false)
                          setMobileSheetOpen(true)
                        }}
                        sx={CALL_DOCK_FAB_SX}
                      >
                        <HistoryIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Box>
                    {/* Video ⇄ Rx/Report toggle */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography
                        component="span"
                        sx={{
                          ...CALL_DOCK_LABEL_SX,
                          background: 'rgba(16,185,129,0.92)',
                          fontWeight: 700,
                        }}
                      >
                        {mobileSheetOpen
                          ? joinAgoraCall
                            ? 'Video'
                            : 'Start video call'
                          : primarySection?.id === 'prescription'
                            ? 'Rx'
                            : 'Report'}
                      </Typography>
                      <IconButton
                        aria-label={
                          mobileSheetOpen
                            ? joinAgoraCall
                              ? 'Show video'
                              : 'Start video call'
                            : 'Open report'
                        }
                        onClick={() => {
                          // From the sheet with no call running, this IS the join button — say so
                          // and start the call directly instead of landing on the dark stage.
                          if (mobileSheetOpen && !joinAgoraCall) {
                            setMobileSheetOpen(false)
                            joinAgoraCallFunction?.()
                            return
                          }
                          // Opening from the default video selection lands on the primary editor —
                          // also select it so its tab pill highlights.
                          if (
                            !mobileSheetOpen &&
                            primarySection?.id &&
                            (!selectedMenuItem || selectedMenuItem === 'video_call')
                          ) {
                            setSelectedMenuItem(primarySection.id)
                          }
                          setMobileSheetOpen((open) => !open)
                        }}
                        sx={{
                          width: 56,
                          height: 56,
                          color: '#fff',
                          background: 'linear-gradient(135deg, #10b981, #059669)',
                          boxShadow: '0 8px 22px rgba(16,185,129,0.45)',
                          border: '1.5px solid rgba(255,255,255,0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #0ea271, #048257)',
                          },
                        }}
                      >
                        {mobileSheetOpen ? (
                          <VideocamIcon sx={{ fontSize: 24 }} />
                        ) : primarySection?.id === 'prescription' ? (
                          <MedicationIcon sx={{ fontSize: 24 }} />
                        ) : (
                          <ArticleIcon sx={{ fontSize: 24 }} />
                        )}
                      </IconButton>
                    </Box>
                  </>
                )}
                {/* Hamburger — minimize / restore the dock */}
                <IconButton
                  aria-label={callDockMinimized ? 'Show controls' : 'Minimize controls'}
                  onClick={() => setCallDockMinimized((m) => !m)}
                  sx={{
                    width: 44,
                    height: 44,
                    color: '#fff',
                    background: 'rgba(15, 15, 18, 0.62)',
                    backdropFilter: 'blur(12px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(12px) saturate(180%)',
                    border: '1px solid rgba(255,255,255,0.22)',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
                    '&:hover': { background: 'rgba(15,15,18,0.78)' },
                  }}
                >
                  {callDockMinimized ? <MenuRoundedIcon /> : <CloseIcon />}
                </IconButton>
              </Box>
            )}

            {/* Print menu (prescription) — Download/Send both save first (prescriptionRef →
                saveAndDownloadPDF awaits savePrescriptionData), exactly like Save. */}
            <Menu
              anchorEl={printMenuAnchor}
              open={Boolean(printMenuAnchor)}
              onClose={() => setPrintMenuAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              sx={{ zIndex: 1900 }}
              PaperProps={{ sx: { minWidth: 224, borderRadius: '12px' } }}
            >
              <MenuItem
                onClick={() => {
                  setPrintMenuAnchor(null)
                  prescriptionRef?.current?.quickPrint?.()
                }}
              >
                <ListItemIcon>
                  <PrintIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Print</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setPrintMenuAnchor(null)
                  prescriptionRef?.current?.downloadPDF?.()
                }}
              >
                <ListItemIcon>
                  <DownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Download PDF</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setPrintMenuAnchor(null)
                  prescriptionRef?.current?.sendPDFToPatient?.()
                }}
              >
                <ListItemIcon>
                  <SendIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Send PDF to patient</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </>
    )
  },
)

export default SessionContainer
